import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { DateField } from "@/components/DateField";
import { PeriodPresets, RANGE_PRESETS, matchPreset } from "@/components/PeriodPresets";
import { ReportPreviewDialog } from "@/components/ReportPreviewDialog";
import { Eye } from "lucide-react";
import { FileSpreadsheet, FileText, ClipboardList, Workflow, Banknote, Mail, TrendingUp, MapPin, Tags, CalendarDays, CalendarRange } from "lucide-react";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);
const MONTHS = [
  { v: 1, l: "Ocak" }, { v: 2, l: "Şubat" }, { v: 3, l: "Mart" }, { v: 4, l: "Nisan" },
  { v: 5, l: "Mayıs" }, { v: 6, l: "Haziran" }, { v: 7, l: "Temmuz" }, { v: 8, l: "Ağustos" },
  { v: 9, l: "Eylül" }, { v: 10, l: "Ekim" }, { v: 11, l: "Kasım" }, { v: 12, l: "Aralık" },
];

const NOW = new Date();
const LAST_MONTH = new Date(NOW.getFullYear(), NOW.getMonth() - 1, 1);
const YM_PRESETS = {
  month: { year: CURRENT_YEAR, month: String(NOW.getMonth() + 1) },
  lastMonth: { year: LAST_MONTH.getFullYear(), month: String(LAST_MONTH.getMonth() + 1) },
  year: { year: CURRENT_YEAR, month: "all" },
  lastYear: { year: CURRENT_YEAR - 1, month: "all" },
};
const byKey = Object.fromEntries(RANGE_PRESETS.map((p) => [p.k, p]));
const REPORT_PRESETS = [
  byKey.today, byKey.week,
  { k: "month", l: "Bu ay" }, { k: "lastMonth", l: "Geçen ay" },
  byKey["3months"],
  { k: "year", l: "Bu yıl" }, { k: "lastYear", l: "Geçen yıl" },
];
const RANGE_ONLY = [byKey.today, byKey.week, byKey["3months"]];
const EMPTY_RANGE = { date_from: "", date_to: "" };

const REPORT_TYPES = [
  { key: "work_logs", label: "Çalışma Kayıtları", icon: ClipboardList, color: "emerald",
    desc: "Köy, kategori, miktar, tutar bazlı aylık/yıllık rapor" },
  { key: "investments", label: "Yatırımlar", icon: Banknote, color: "purple",
    desc: "Bütçe - Gerçekleşen karşılaştırma, köy/kurum bazlı" },
  { key: "infrastructure", label: "Altyapı Projeleri", icon: Workflow, color: "blue",
    desc: "İçme suyu, kanalizasyon, sulama projeleri durum raporu" },
  { key: "petitions", label: "Dilekçeler", icon: Mail, color: "orange",
    desc: "Dilekçe, arıza ve etüt kayıtları" },
  { key: "year_comparison", label: "Yıllar Arası Karşılaştırma", icon: TrendingUp, color: "rose",
    desc: "Önceki yıl / cari yıl / sonraki yıl karşılaştırmalı rapor" },
];

const MATERIAL_REPORTS = [
  { key: "material_village", label: "Köy Bazında Malzeme Maliyeti", icon: MapPin, color: "emerald",
    desc: "Her köyde kullanılan malzemeler, miktarları ve köy toplamları" },
  { key: "material_category", label: "İş Kategorisi Bazında Malzeme Maliyeti", icon: Tags, color: "blue",
    desc: "Her iş kategorisinde kullanılan malzemeler ve kategori toplamları" },
  { key: "material_monthly", label: "Aylara Göre Malzeme Maliyeti", icon: CalendarRange, color: "purple",
    desc: "Seçilen yılın ya da tarih aralığının her ayı için toplam malzeme harcaması" },
  { key: "material_daily", label: "Günlük Malzeme Harcamaları", icon: CalendarDays, color: "orange",
    desc: "Her malzeme girişi tarih, köy ve kategoriyle tek tek" },
];

const ReportCard = ({ r, onDownload, onPreview }) => (
  <Card className="p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`report-card-${r.key}`}>
    <div className="flex items-start gap-4">
      <div className={`h-11 w-11 rounded-md bg-${r.color}-50 text-${r.color}-700 flex items-center justify-center flex-shrink-0`}>
        <r.icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-display font-semibold text-slate-900">{r.label}</h3>
        <p className="text-sm text-slate-500 mt-1">{r.desc}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button size="sm" onClick={() => onPreview(r.key)} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`preview-${r.key}`}>
            <Eye className="h-3.5 w-3.5 mr-1.5" /> Önizle
          </Button>
          <Button variant="outline" size="sm" onClick={() => onDownload(r.key, "pdf")} data-testid={`export-pdf-${r.key}`}>
            <FileText className="h-3.5 w-3.5 mr-1.5 text-rose-600" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => onDownload(r.key, "excel")} data-testid={`export-excel-${r.key}`}>
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5 text-emerald-600" /> Excel
          </Button>
        </div>
      </div>
    </div>
  </Card>
);

export default function ReportsPage() {
  const [villages, setVillages] = useState([]);
  const [insts, setInsts] = useState([]);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState("all");
  const [villageId, setVillageId] = useState("all");
  const [institutionId, setInstitutionId] = useState("all");
  const [range, setRange] = useState(EMPTY_RANGE);
  const hasRange = !!(range.date_from || range.date_to);

  const pickPreset = (p) => {
    if (YM_PRESETS[p.k]) {
      setYear(YM_PRESETS[p.k].year);
      setMonth(YM_PRESETS[p.k].month);
      setRange(EMPTY_RANGE);
    } else {
      setRange(p.range());
    }
  };
  const activePreset = hasRange ? matchPreset(RANGE_ONLY, range)
    : Object.keys(YM_PRESETS).find((k) => YM_PRESETS[k].year === year && YM_PRESETS[k].month === month);

  useEffect(() => {
    api.get("/villages").then((r) => setVillages(r.data));
    api.get("/institutions").then((r) => setInsts(r.data));
  }, []);

  const [preview, setPreview] = useState(null);

  const buildParams = () => {
    const params = new URLSearchParams();
    if (year) params.append("year", year);
    if (month !== "all") params.append("month", month);
    if (villageId !== "all") params.append("village_id", villageId);
    if (institutionId !== "all") params.append("institution_id", institutionId);
    if (range.date_from) params.append("date_from", range.date_from);
    if (range.date_to) params.append("date_to", range.date_to);
    return params;
  };

  const openPreview = async (type) => {
    setPreview({ type, loading: true });
    try {
      const r = await api.get(`/reports/${type}/preview?${buildParams()}`);
      setPreview({ type, data: r.data });
    } catch (e) {
      setPreview(null);
      toast.error(e?.response?.data?.detail?.toString?.() || "Önizleme oluşturulamadı");
    }
  };

  const download = async (type, fmt) => {
    const params = buildParams();
    const token = localStorage.getItem("khgb_token");
    try {
      const resp = await fetch(`${API}/reports/${type}/${fmt}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(typeof err.detail === "string" ? err.detail : "Rapor oluşturulamadı");
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}_${hasRange ? `${range.date_from || "baslangic"}_${range.date_to || "bugun"}` : `${year}${month !== "all" ? "_" + month : ""}`}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Rapor indirildi");
    } catch (e) {
      toast.error(e.message || "Hata");
    }
  };

  return (
    <AppLayout
      title="Raporlar"
      subtitle="PDF ve Excel formatında rapor oluşturma"
    >
      <Card className="p-6 border border-slate-200 shadow-sm mb-6" data-testid="report-filters">
        <h3 className="font-display font-semibold text-slate-900 mb-3">Filtreler</h3>
        <div className="mb-4">
          <PeriodPresets presets={REPORT_PRESETS} active={activePreset} onPick={pickPreset} testPrefix="report" />
        </div>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <Label className="text-xs text-slate-600">Başlangıç tarihi</Label>
            <DateField value={range.date_from} onChange={(v) => setRange((r) => ({ ...r, date_from: v }))} testId="report-date-from" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Bitiş tarihi</Label>
            <DateField value={range.date_to} onChange={(v) => setRange((r) => ({ ...r, date_to: v }))} testId="report-date-to" />
          </div>
          {hasRange && (
            <Button variant="ghost" size="sm" onClick={() => setRange(EMPTY_RANGE)} data-testid="report-range-clear">Aralığı temizle</Button>
          )}
        </div>
        {hasRange && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4" data-testid="report-range-note">
            Tarih aralığı seçiliyken raporlar yıl ve ay yerine bu aralığa göre hazırlanır (Yıllar Arası Karşılaştırma hariç).
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs text-slate-600">Yıl</Label>
            <Select value={String(year)} onValueChange={(v) => { setYear(Number(v)); setRange(EMPTY_RANGE); }} disabled={hasRange}>
              <SelectTrigger data-testid="report-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Ay</Label>
            <Select value={month} onValueChange={(v) => { setMonth(v); setRange(EMPTY_RANGE); }} disabled={hasRange}>
              <SelectTrigger data-testid="report-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Yıl</SelectItem>
                {MONTHS.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Köy</Label>
            <Select value={villageId} onValueChange={setVillageId}>
              <SelectTrigger data-testid="report-village-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Köyler</SelectItem>
                {villages.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Kurum</Label>
            <Select value={institutionId} onValueChange={setInstitutionId}>
              <SelectTrigger data-testid="report-institution-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kurumlar</SelectItem>
                {insts.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <h3 className="font-display font-semibold text-slate-900 mb-3">Genel Raporlar</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {REPORT_TYPES.map((r) => <ReportCard key={r.key} r={r} onDownload={download} onPreview={openPreview} />)}
      </div>

      <h3 className="font-display font-semibold text-slate-900 mt-10 mb-1" data-testid="material-reports-heading">Malzeme Maliyet Raporları</h3>
      <p className="text-xs text-slate-500 mb-3">Yıl, ay, tarih aralığı ve köy filtreleri bu raporlara da uygulanır. Kurum filtresi kullanılmaz.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {MATERIAL_REPORTS.map((r) => <ReportCard key={r.key} r={r} onDownload={download} onPreview={openPreview} />)}
      </div>
      <ReportPreviewDialog preview={preview} onClose={() => setPreview(null)} onDownload={download} />
    </AppLayout>
  );
}
